import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {debounceTime, takeUntil, tap} from 'rxjs/operators';
import {combineLatest, Observable, Subject} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {HolidayPrefetchService} from '../../../shared/services/holiday-prefetch.service';
import {IPrefetchDateParams} from '../../../shared/interfaces/iprefetch-date-params';
import * as dayjs from 'dayjs';
import {HolidayDetailService} from '../holiday-detail-page/services/holiday-detail.service';
import {IHolidayDetail} from '../holiday-detail-page/interfaces/iholiday-detail';
import {IHolidayDetailSolution} from '../holiday-detail-page/interfaces/iholiday-detail-solution';
import {WidgetService} from '../../../shared/services/widget.service';
import {BookingService} from '../../../shared/services/booking.service';
import {HolidayBookingService} from '../holiday-booking-page/services/holiday-booking.service';
import {Occupancy} from '../../../shared/interfaces/occupancy';
import {FormatAgesPipe} from '../../../shared/pipes/format-ages.pipe';
import {GetDataFromMappingPipe} from '../../../shared/pipes/get-data-from-mapping.pipe';
import sanitizeForUrl from '../../../../utils/sanitizeForUrl';
import {LoadingItem} from '../../../shared/interfaces/loading-item';
import {LoadingService} from '../../../shared/services/loading.service';
import {CmsService} from '../../../cms/services/cms.service';
import {SEOService} from '../../../shared/services/seo.service';


export interface HolidayDetailGenericPageSearchParams {
  tour_id?: number;
  accommodation_id?: number;
  source?: string;
  topList?: number[];
  pluginSource?: number;
  date_from?: string;
  date_to?: string;
  ages?: number[];
}

@Component({
  selector: 'holiday-detail-generic-page',
  templateUrl: './holiday-detail-generic-page.component.html',
  styleUrls: ['./holiday-detail-generic-page.component.scss']
})
export class HolidayDetailGenericPageComponent implements OnInit, OnDestroy {

  isWidget$: Observable<boolean>;

  private unsubscribe$ = new Subject();
  public newApiCallDebouncer$: Subject<boolean> = new Subject<boolean>();

  public isTour: boolean;
  public allAvailableDates: Date[];
  public detailSearchParams: HolidayDetailGenericPageSearchParams;
  public isLoadingDetail: boolean;
  public isDebounceing: boolean;
  public noDates: boolean;
  public fixedRecap = false;
  public holidayDetails: IHolidayDetail;
  public allSolutionsToShow: IHolidayDetailSolution[];
  public loadingItem: LoadingItem = {id: 'holiday-detail', message: 'Stiamo preparando l\'offerta per te...'};

  public selectedSolution: IHolidayDetailSolution;
  public selectedOccupancy: Occupancy;
  public selectedDepartureDate: Date;
  public selectedDeparturePlaceId: string;
  public brand: any;
  public selectedDuration: number;
  public bookingUrl: string;
  public formattedGuestsString: string;
  public destinationName: string;
  public fallbackPrice = null;
  public isLanding: boolean;

  public accomodationParentId: number;

  constructor(
    private _route: ActivatedRoute,
    private _holidayPrefetchService: HolidayPrefetchService,
    private _holidayDetailService: HolidayDetailService,
    private _seoService: SEOService,
    private _widgetService: WidgetService,
    private _cmsService: CmsService,
    private _formatAges: FormatAgesPipe,
    private _getDataFromMapping: GetDataFromMappingPipe,
    private _bookingService: BookingService,
    private _holidayBookingService: HolidayBookingService,
    private _loadingService: LoadingService
  ) {
  }

  //  Gestione del recap fisso sul bottom della view da mobile
  @HostListener('window:scroll', ['$event'])

  onWindowScroll($event) {
    this.fixedRecap = window.scrollY >= 100 ? true : false;
    //  Per evitare che il recap fisso sul bottom copra parte del footer aggiungo a quest'ultimo un margin-bottom della stessa altezza del recap
    const recapContainer = (document.querySelector('div.detail-recap__container') as HTMLElement);
    if (!recapContainer) {
      return;
    }
    const offset = recapContainer?.offsetHeight;
    if (window.innerWidth <= 992 && this.fixedRecap && document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = offset + 'px';
    } else if (document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = '0';
    }
  }

  ngOnInit(): void {
    const routeSegments = this._route.snapshot['_urlSegment'].segments;
    this.isLanding = routeSegments[0].path == 'landing';
    this.isWidget$ = this._widgetService.isWidget();

    this._cmsService.brandConfig$.subscribe(brand => {
      if (brand) {
        this.brand = brand;
      }
    });

    this._holidayBookingService.departureDateAccordionTrigger$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        const priceGrid = document.querySelector('.accomodation-tab__solutions');
        if (!priceGrid) {
          return;
        }
        priceGrid.scrollIntoView({
          behavior: 'smooth'
        });
      });

    this._holidayBookingService.holidayDetailLoadingStatus$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((loadingStatus) => {
        this.isLoadingDetail = loadingStatus.isLoadingDetail;
        this.isDebounceing = loadingStatus.isDebounceing;
      });


    //  Handling routeParams and queryParams
    combineLatest(
      [
        this._route.queryParams,
        this._route.params
      ]
    )
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(100)
      )
      .subscribe((params) => {
        this.isTour = this.isLanding ? routeSegments[1] == 'tour' : this._route?.snapshot?.data?.type?.toLowerCase() === 'tour';
        this.allAvailableDates = [];
        this.noDates = false;

        this.detailSearchParams = this.isLanding ? this.getLandingSearchParams(routeSegments) :
          this.getFormattedParamsFromMergedRouteParams(params);

        if (!this.detailSearchParams?.accommodation_id && !this.detailSearchParams?.tour_id) {
          console.error('Errore. Non è stato possibile ricavare l\'id del prodotto');
          return;
        }

        this.isTour = this.detailSearchParams?.tour_id > 0; //  Se la formattazione dei parametri ha restituito un tour_id il prodotto è un tour

        this.getAllAvailableDates()
          .finally(() => {

            //  Dopo aver recuparato le date di partenza mando il next su occupancy e departure date.
            //  Ciò attiverà il debouncer e farà partire prima chiamata di dettaglio
            // this._bookingService.setNewAges(this.detailSearchParams.ages);
            // this._holidayBookingService.setData('selectedDepartureDate', this.detailSearchParams.date_from);
            this.onSelectedParamsChange();
          });

      });

    this.newApiCallDebouncer$
      .pipe(
        tap(() => {
          //  Loader a tutto schermo solo se non ci sono ancora holidayDetails (prima chiamata)
          if (!this.holidayDetails) {
            this._loadingService.addLoad(this.loadingItem);
          }
          this._holidayBookingService.setDetailLoadingStatus(false, false, true);
        }),
        takeUntil(this.unsubscribe$),
        debounceTime(1000),
      )
      .subscribe(() => {
        this._holidayBookingService.setDetailLoadingStatus();
        if(this.allAvailableDates.length > 0){
           this.onSelectedParamsChange(); //  Nuova chiamata di dettaglio
        }
      });

    this._bookingService.occupancy$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(occupancy => {
        this.selectedOccupancy = occupancy;
        this.newApiCallDebouncer$.next(true);
        if (!this.selectedOccupancy) {
          return;
        }
        this.updateOccupnacyString();
      });

    this._holidayBookingService.selectedDepartureDate$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((departureDate: Date) => {
        this.selectedDepartureDate = departureDate;
        this.newApiCallDebouncer$.next(true);
      });

    this._holidayBookingService.selectedSolution$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((selectedSolution: IHolidayDetailSolution) => {
        this.selectedSolution = selectedSolution;

        if (!this.selectedSolution) {
          return;
        }

        this.updateBookingUrl();

        if (this.selectedSolution?.journey) {
          return; // ci sono già le liveInfo
        }

        this._holidayBookingService.setDetailLoadingStatus(false, true);

        this._holidayBookingService.getSolutionAdditionalLiveInfo(this.selectedSolution)
          .catch(e => {
            console.error('La SelectedSolution non è disponibile');
          })
          .finally(() => {
            this._holidayBookingService.setDetailLoadingStatus();
            // this._loadingService.removeClassicLoad(loadingItemId);
          });
      });
  }

  private getLandingSearchParams(segments: any[]): HolidayDetailGenericPageSearchParams {
    const detailSearchParams = {} as HolidayDetailGenericPageSearchParams;
    detailSearchParams[this.isTour ? 'tour_id' : 'accommodation_id'] = segments[4].path;
    return detailSearchParams;
  }

  private getFormattedParamsFromMergedRouteParams(rawRouteParams: any[]): HolidayDetailGenericPageSearchParams {
    try {
      const merged: any = {};
      // RouteParams e QueryParams mergiati in un unico oggetto
      rawRouteParams.forEach(param => {
        Object.keys(param).forEach(key => {
          merged[key] = param[key];
        });
      });

      if (!merged?.productId || !Number(merged.productId)) {
        throw new Error('productId non fornito');
      }
      const detailSearchParams = {} as HolidayDetailGenericPageSearchParams;
      detailSearchParams[this.isTour ? 'tour_id' : 'accommodation_id'] = Number(merged.productId);
      //  Impostazione Occupancy da queryParams o su valori di default
      const agesArray = merged?.ages?.split(',')?.map(age => Number(age)) || null;
      if (agesArray?.every((age: number) => (age || age === 0))) {
        detailSearchParams.ages = agesArray;
      } else {
        detailSearchParams.ages = [35, 35];
      }
      if (merged.dateFrom && dayjs(merged.dateFrom, 'DD-MM-YYYY').isValid()) {
        detailSearchParams.date_from = dayjs(merged.dateFrom, 'DD-MM-YYYY').format('YYYY-MM-DD');
      }
      if (merged.duration && Number(merged.duration)) {
        this.selectedDuration = Number(merged.duration); // solo per identificare searchSolution
      }
      if (merged.packetId) {
        this.detailSearchParams.source = merged.packetId;
      }
      if (merged.pluginSource && Number(merged.pluginSource)) {
        this.detailSearchParams.pluginSource = Number(merged.pluginSource);
      }
      if (merged.topId && Number(merged.topId)) {
        this.detailSearchParams.topList = [Number(merged.topId)];
      }
      if (merged.departurePlace) {
        this.selectedDeparturePlaceId = merged.departurePlace;
      }

      return detailSearchParams;

    } catch (err) {
      console.error(err);
      return null;
    }
  }

  public onSelectedMonthChange(newSampleDate: Date) {
    const {date_from, date_to} = this.monthExtremeDatesFromSingleDate(newSampleDate);
    this.detailSearchParams.date_from = date_from;
    this.detailSearchParams.date_to = date_to;

    setTimeout(()=> {
      this.newHolidayDetail();
    },0)
  }

  public onSelectedParamsChange(): void {
    const isFirstApiCall = !this.holidayDetails;

    //  RESET ALL
    this.allSolutionsToShow = [];
    this._holidayBookingService.setData('selectedSolution', null);

    //  Aggiungo ai parametri i campi variabili
    const queryDate = this.selectedDepartureDate &&
    dayjs(this.selectedDepartureDate).isValid() &&
    dayjs(this.selectedDepartureDate).isAfter(dayjs()) ?
      this.selectedDepartureDate :
      null; // Contollo validità della eventuale data ricevuto come queryParm

    //  Casistica WTG - bisognerà fare una chiamata ad un mese
    if (this.brand?.detailPageConfig?.showPriceGrid) {
      const firstUsefulDate = queryDate || this.allAvailableDates.find((date: Date) => dayjs(date)?.isValid());
      const {date_from, date_to} = this.monthExtremeDatesFromSingleDate(firstUsefulDate);
      this.detailSearchParams.date_from = date_from;
      this.detailSearchParams.date_to = date_to;
    } else if (queryDate) {
      // Casistica WTG
      this.detailSearchParams.date_from = dayjs(queryDate).format('YYYY-MM-DD');
      this.detailSearchParams.date_to = this.detailSearchParams.date_from;
    }

    this.detailSearchParams.ages = this.selectedOccupancy.ages;

    //  Aggiungo i parametri sourceId, topId e pluginSource derivati da un'eventuale chiamata precedente, se disponibili
    //  e non ancora impostati globalmente. Ciò renderà le chiamate di dettaglio più veloci.

    if (!this.detailSearchParams?.source || !this.detailSearchParams?.pluginSource || !this.detailSearchParams?.topList?.length) {
      if (this.holidayDetails?.solutions?.length && this.holidayDetails?.solutions[0].source) {
        this.detailSearchParams.source = this.holidayDetails.solutions[0].source;
      }
      if (this.holidayDetails?.pluginSource) {
        this.detailSearchParams.pluginSource = this.holidayDetails.pluginSource;
      }

      const  topId = this.holidayDetails?.realTourOperatorID ||this.holidayDetails?.tourOperatorId;
      if (topId) {
        this.detailSearchParams.topList = [topId];
      }
    }

    this.newHolidayDetail();
  }

  //  Passata una data puntuale restituisce primo ed ultimo giorno del relativo mese
  //  Se primo giorno del mese è una data passata la rimpiazza con il giorno successivo alla data attuale
  private monthExtremeDatesFromSingleDate(singleDate) {
    const tomorrow = dayjs().add(1, 'day');
    const startOfselectedMonth = dayjs(singleDate).startOf('month');
    const dayJsDateFrom = startOfselectedMonth.isBefore(tomorrow) ? tomorrow : startOfselectedMonth;
    return {
      date_from: dayJsDateFrom.format('YYYY-MM-DD'),
      date_to: dayJsDateFrom.endOf('month').format('YYYY-MM-DD')
    };
  }
//  Imposta titolo immagine e descrizione della pagina
  setSeoProperties(holiday) {
    try {
      this._seoService.setTitle(holiday.title);
      this._seoService.setImage(holiday.images[0]);
      this._seoService.setDescription(
        holiday.accomodation?.texts?.map(d => d.description).join(',') ||
        holiday.tour?.texts?.map(d => d.description).join(',')
    );
    } catch (error) {
      console.error("SEO ERROR", error);
    }

  }

  private newHolidayDetail() {

    this.allSolutionsToShow = [];
    this._holidayBookingService.setDetailLoadingStatus(true);

    let callAgainWithParentAccommodation = false;

    this._holidayDetailService.getHolidayGenericDetail(this.detailSearchParams).toPromise()
      .then((newHolidayDetail: IHolidayDetail) => {
        setTimeout(() => {
          this.holidayDetails = newHolidayDetail;

          const priceShownMustBeLive = newHolidayDetail.pluginSource === 52 ||
          (newHolidayDetail?.tourOperatorId === 1101 && newHolidayDetail?.realTourOperatorID === 1016058);

          if (priceShownMustBeLive) {
            this.holidayDetails?.solutions?.forEach((solution: IHolidayDetailSolution) => {
              solution.priceShownMustBeLive = priceShownMustBeLive;
            });

            this.fallbackPrice = null
          }

          this.allSolutionsToShow = newHolidayDetail?.solutions;
          this.setDestinationName();
          try {
            this.setBestSolution(this.selectedDuration, this.selectedDeparturePlaceId);
          } catch (error) {
            console.error("ERROR", error);
          }

        }, 0);

        //  Se la dettaglio ha restituito una holiday con useAccommodationParent a true
        //  La chiamata va ripetuta sostituendo l'accommodationId con il campo accommodationParentId, ricavato anch'esso dalla dettaglio.
        if (newHolidayDetail.useAccommodationParent) {
          this.accomodationParentId = newHolidayDetail.accommodationParentId

          callAgainWithParentAccommodation = newHolidayDetail.useAccommodationParent;
          this.detailSearchParams.accommodation_id = newHolidayDetail.accommodationParentId;
          return Promise.resolve(); //  see finally()
        }

        this.setSeoProperties(this.holidayDetails);

        if (!this.detailSearchParams?.date_from) {
          //  Mi fermo qui, perché il payload non conteneva la data. i.e. quella appena effettuata era una ricerca generica
          //  e non si può ricavare nè lista di soluzioni disponibili né tantomeno selectedSolution.
          return;
        }

        //  Se era una chiamata con data/e spefifica/he

        if (!this.holidayDetails?.solutions?.length) {
          //  ...Ma la chiamata non ha restituito comunque solutions
          this.selectedSolution = null
          console.error('nessuna solution disponibile')
          throw new Error('Cannot get solution for new selected date');
        }
      })
      .catch((err: Error) => {
        console.warn(err);
        if (this.holidayDetails) {
          return;
        }
      })
      .finally(() => {
        this._loadingService.removeLoad(this.loadingItem);
        this._holidayBookingService.setDetailLoadingStatus();

        if (callAgainWithParentAccommodation) {
          //  Se si era entrati nella casistica della nuova dettaglio con l'accommodationIdParent re-invoca la funzione per ripetere la chiamata
          //  Il parametro relativo all'accommodationId è stato già sostituito con quello corretto nella variabile globale
          this.newHolidayDetail();
        }
      });
  }

  //  Questa richiamata dal componente figlio detailSolutions
  public livePricesLoadingCompletedOnChildComponent() {
    this.setBestSolution(this.selectedDuration, this.selectedDeparturePlaceId);
  }

  public setBestSolution(duration: number, selectedDeparturePlaceId: string) {
    let selectedSolution: IHolidayDetailSolution;
    
    if (!duration || !selectedDeparturePlaceId) {
      const solEarlier = this.allSolutionsToShow.sort((solA, solB) => dayjs(solA.dateFrom).valueOf() - dayjs(solB.dateFrom).valueOf())[0];
      selectedSolution = solEarlier;
    } else {
      selectedSolution = this.allSolutionsToShow.find((solution: IHolidayDetailSolution) => {
        return this.durationMatches(duration, solution.nights) && this.departurePlaceMatches(selectedDeparturePlaceId, solution.airport);
      });
      if (!selectedSolution) {
        return;
      }
    }

    this._holidayBookingService.selectedSolution$.next(selectedSolution);
  }

  private durationMatches(selectedDuration: number, solutionDuration: number): boolean {
    return Number(solutionDuration) && Number(selectedDuration) && (Number(solutionDuration) === Number(selectedDuration));
  }

  private departurePlaceMatches(selectedDeparturePlaceId: string, solutionAirport: number): boolean {
    //  Se solo-soggiorno restituisce true qualora non esistesse solutionAirport
    if (selectedDeparturePlaceId === 'solo-soggiorno') {
      return !solutionAirport;
    }
    //  selectedDeparturePlaceId non valido benché non sia solo-soggiorno
    if (!Number(selectedDeparturePlaceId)) {
      return false;
    }
    return Number(solutionAirport) === Number(selectedDeparturePlaceId);
  }

  private getAllAvailableDates(): Promise<any> {
    this.allAvailableDates = [];
    const prefetchDateParams = {} as IPrefetchDateParams;
    if (this.detailSearchParams.tour_id) {
      prefetchDateParams.tours = [this.detailSearchParams.tour_id];
    } else if (this.detailSearchParams.accommodation_id) {
      prefetchDateParams.accommodations = [this.detailSearchParams.accommodation_id];
    }

    return this._holidayPrefetchService.prefetchDate(prefetchDateParams).toPromise()
      .then((dates: string[]) => {
        if (!dates?.length) {
          throw new Error('No available dates!');
        }
        this.allAvailableDates = [...new Set(dates)]
          ?.map((stringDate: string) => {
            return dayjs(stringDate, 'YYYY-MM-DD').isValid() ?
              dayjs(stringDate, 'YYYY-MM-DD').toDate() :
              null;
          })
          ?.filter((validDate: Date) => validDate);

        if (!this.allAvailableDates?.length) {
          this.noDates = true;
        }
      })
      .catch((err: Error) => {
        console.warn(err);
        this.noDates = true;
      });
  }

  ngOnDestroy(): void {
    this._holidayBookingService.setDetailLoadingStatus();
    this._holidayBookingService.setData('selectedDepartureDate', null);
    this._holidayBookingService.setData('selectedSolution', null);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private updateBookingUrl(): void {
    if (!this.selectedSolution || !this.holidayDetails) {
      this.bookingUrl = null;
      return;
    }
    const packetId = this.selectedSolution.source;
    const dateFrom = dayjs(this.selectedSolution.dateFrom).format('DD-MM-YYYY');
    const agesString = this.selectedOccupancy?.ages?.join(',');
    const productId = this.detailSearchParams?.accommodation_id || this.detailSearchParams?.tour_id;
    const topId = this.holidayDetails?.tourOperatorId || null;
    const pluginSource = this.holidayDetails?.pluginSource;
    const title = sanitizeForUrl(this.holidayDetails?.title);
    this.bookingUrl = `/vacanze/booking/${productId}/${topId}/${packetId}/${pluginSource}/struttura/${title}/dal/${dateFrom}/al/${dateFrom}/eta/${agesString}`;
  }

  private updateOccupnacyString(): void {
    this.formattedGuestsString = this._formatAges.transform(this.selectedOccupancy?.ages, 'adN_chN');
  }

  //  Trova il nome della località di destinazione
  private setDestinationName(): void {
    if (this.destinationName?.length) {
      return; // C'è già
    }
    this.destinationName = this.holidayDetails?.place?.name || '';
    if (!this.holidayDetails?.place?.parentId) {
      return;
    }
    this._getDataFromMapping.transform(this.holidayDetails?.place?.parentId, 'destination')
      .toPromise()
      .then(result => {
        if (result?.Name?.length > 1) {
          this.destinationName += `, ${result?.Name}`;
        }
      });
  }

}
